import React, { useState, useEffect } from "react";
import { 
  getUserStats, 
  getUsers, 
  inviteUser,
  bulkInviteUsers,
  pollBulkJobStatus,
  updateUserStatus, 
  deleteUser,
  assignExamsToUsers 
} from "@/services/user.util";
import UserStats from "./components/UserStats";
import InviteUserFormTab from "./components/InviteUserFormTab";
import BulkInviteFormTab from "./components/BulkInviteFormTab";
import BulkUploadResults from "./components/BulkUploadResults";
import BulkProgressModal from "./components/BulkProgressModal";
import DataGridUserTable from "./components/DataGridUserTable";
import ExamAssignmentModal from "./components/ExamAssignmentModal";

const UserManagement = () => {
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeStudents: 0,
    activeInstructors: 0,
    suspendedUsers: 0
  });
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeInviteTab, setActiveInviteTab] = useState('individual');
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  
  // User selection and exam assignment state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showExamAssignment, setShowExamAssignment] = useState(false);
  const [isExamSelectionMode, setIsExamSelectionMode] = useState(false);
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    status: ''
  });
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [pageSize] = useState(20); // Increased page size for better UX

  // Fetch user statistics
  const fetchUserStats = async () => {
    try {
      const response = await getUserStats();
      if (response.success) {
        setUserStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  // Fetch users list with pagination and filters
  const fetchUsers = async (page = currentPage, search = searchTerm, userFilters = filters, sorting = sortConfig) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        search: search.trim(),
        role: userFilters.role,
        status: userFilters.status,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder
      };
      
      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await getUsers(params);
      if (response.success) {
        // Add row numbers to users for better performance
        const usersWithRowNumbers = response.data.users.map((user, index) => ({
          ...user,
          rowNumber: ((page - 1) * pageSize) + index + 1
        }));
        setUsers(usersWithRowNumbers);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to fetch users' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchUsers(newPage, searchTerm, filters, sortConfig);
  };

  // Handle search change with debouncing
  const handleSearchChange = (search) => {
    setSearchTerm(search);
    setCurrentPage(1); // Reset to first page on search
    
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchUsers(1, search, filters, sortConfig);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
    fetchUsers(1, searchTerm, newFilters, sortConfig);
  };

  // Handle sort change
  const handleSortChange = (sortBy, sortOrder) => {
    const newSortConfig = { sortBy, sortOrder };
    setSortConfig(newSortConfig);
    setCurrentPage(1); // Reset to first page on sort change
    fetchUsers(1, searchTerm, filters, newSortConfig);
  };

  // Handle user invitation
  const handleInviteUser = async (formData) => {
    try {
      const response = await inviteUser(formData);
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: 'User invitation sent successfully!' 
        });
        // Refresh users list and stats
        fetchUsers();
        fetchUserStats();
        return { success: true };
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to send invitation' 
      });
      return { success: false, error: error.message };
    }
  };

  // Handle bulk user invitation
  const handleBulkInviteUsers = async (users) => {
    try {
      const response = await bulkInviteUsers(users);
      
      if (response.success) {
        // Check if this is an async job
        if (response.data.isAsync) {
          // Start progress tracking for async job
          const jobId = response.data.jobId;
          
          setBulkProgress({
            jobId,
            status: 'processing',
            progress: {
              total: users.length,
              processed: 0,
              successful: 0,
              failed: 0,
              duplicates: 0,
              emailsSent: 0,
              emailsFailed: 0
            },
            startTime: new Date(),
            estimatedTime: response.data.estimatedTime
          });

          setMessage({ 
            type: 'info', 
            text: `Processing ${users.length} users asynchronously. Progress will be shown in the modal.` 
          });

          // Start polling for job status
          try {
            const jobResult = await pollBulkJobStatus(jobId, (jobData) => {
              setBulkProgress(prev => ({
                ...prev,
                ...jobData,
                duration: jobData.duration || (Date.now() - prev.startTime.getTime())
              }));
            });

            // Job completed successfully
            setBulkResults({
              summary: jobResult.summary,
              results: jobResult.results
            });
            setBulkProgress(null);
            setMessage({ 
              type: 'success', 
              text: `Bulk invitation completed! ${jobResult.progress.successful} users invited successfully.` 
            });

          } catch (pollError) {
            setBulkProgress(null);
            setMessage({ 
              type: 'error', 
              text: `Job processing error: ${pollError.message}` 
            });
          }

        } else {
          // Synchronous response (small batch)
          setBulkResults(response.data);
          setMessage({ 
            type: 'success', 
            text: response.message 
          });
        }

        // Refresh users list and stats
        fetchUsers();
        fetchUserStats();
        return { success: true };
        
      } else {
        setBulkResults(response.data);
        setMessage({ 
          type: 'warning', 
          text: response.message 
        });
        return { success: false };
      }
    } catch (error) {
      setBulkProgress(null);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to process bulk invitation' 
      });
      return { success: false, error: error.message };
    }
  };

  // Handle user suspension/activation
  const handleUserStatus = async (userId, isActive) => {
    try {
      const response = await updateUserStatus(userId, isActive);
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: `User ${isActive ? 'activated' : 'suspended'} successfully!` 
        });
        fetchUsers(); // Refresh current page
        fetchUserStats();
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update user status' 
      });
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const response = await deleteUser(userId);
        if (response.success) {
          setMessage({ 
            type: 'success', 
            text: 'User deleted successfully!' 
          });
          fetchUsers(); // Refresh current page
          fetchUserStats();
        }
      } catch (error) {
        setMessage({ 
          type: 'error', 
          text: error.message || 'Failed to delete user' 
        });
      }
    }
  };

  // Handle user selection change
  const handleUserSelectionChange = (selectedUserIds) => {
    setSelectedUsers(selectedUserIds);
  };

  // Toggle exam selection mode
  const toggleExamSelectionMode = () => {
    if (isExamSelectionMode) {
      // Exiting selection mode, clear selections
      setSelectedUsers([]);
    }
    setIsExamSelectionMode(!isExamSelectionMode);
  };

  // Handle opening exam assignment modal
  const handleOpenExamAssignment = () => {
    if (selectedUsers.length === 0) {
      setMessage({
        type: 'warning',
        text: 'Please select at least one user to assign exams'
      });
      return;
    }
    setShowExamAssignment(true);
  };

  // Handle exam assignment
  const handleAssignExams = async (userIds, examIds) => {
    try {
      const response = await assignExamsToUsers(userIds, examIds);
      if (response.success) {
        setMessage({
          type: 'success',
          text: response.message
        });
        // Refresh users list
        fetchUsers();
        fetchUserStats();
        // Reset selection
        setSelectedUsers([]);
      } else {
        setMessage({
          type: 'warning',
          text: response.message
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to assign exams'
      });
      throw error; // Re-throw to let the modal handle it
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    fetchUserStats();
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="row y-gap-30">
      <div className="col-12">
        <h4 className="text-20 lh-1 fw-500 mb-20">User Management</h4>
        <p className="text-15 text-light-1 mb-30">
          Invite new users individually or via CSV bulk upload, and manage existing users
        </p>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className="col-12">
          <div className={`alert ${
            message.type === 'success' ? 'alert-success' : 
            message.type === 'warning' ? 'alert-warning' : 
            message.type === 'info' ? 'alert-info' : 'alert-danger'
          } mb-20`}>
            {message.text}
          </div>
        </div>
      )}

      {/* User Statistics */}
      <div className="col-lg-6">
        <UserStats stats={userStats} />
      </div>

      {/* Invite Section */}
      <div className="col-lg-6">
        <div className="bg-white border-light rounded-16 px-30 py-30 shadow-4">
          <div className="tabs -active-purple-2 js-tabs">
            <div className="tabs__controls d-flex x-gap-20 y-gap-10 flex-wrap items-center mb-20">
              <button
                className={`tabs__button text-light-1 ${activeInviteTab === 'individual' ? 'is-active' : ''}`}
                onClick={() => setActiveInviteTab('individual')}
                type="button"
              >
                Individual Invite
              </button>
              <button
                className={`tabs__button text-light-1 ${activeInviteTab === 'bulk' ? 'is-active' : ''}`}
                onClick={() => setActiveInviteTab('bulk')}
                type="button"
              >
                Bulk Upload
              </button>
            </div>

            <div className="tabs__content">
              {/* Individual Invite Tab */}
              <div className={`tabs__pane ${activeInviteTab === 'individual' ? 'is-active' : ''}`}>
                <InviteUserFormTab onSubmit={handleInviteUser} />
              </div>

              {/* Bulk Upload Tab */}
              <div className={`tabs__pane ${activeInviteTab === 'bulk' ? 'is-active' : ''}`}>
                <BulkInviteFormTab onSubmit={handleBulkInviteUsers} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exam Assignment Controls */}
      {isExamSelectionMode && (
        <div className="col-12">
          <div className="bg-purple-3 border-light rounded-16 px-30 py-20 mb-20">
            <div className="d-flex items-center justify-between">
              <div className="d-flex items-center">
                <div className="size-40 d-flex justify-center items-center bg-purple-1 rounded-full mr-15">
                  <i className="text-16 icon-check text-white"></i>
                </div>
                <div>
                  <h5 className="text-18 lh-1 fw-500 text-purple-1">Exam Assignment Mode</h5>
                  <div className="text-14 text-purple-1 mt-5">
                    Select users to assign exams. {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected.
                  </div>
                </div>
              </div>
              <div className="d-flex items-center x-gap-15">
                {selectedUsers.length > 0 && (
                  <button
                    onClick={handleOpenExamAssignment}
                    className="button -sm bg-purple-1 text-white fw-500"
                  >
                    Assign Exams ({selectedUsers.length})
                  </button>
                )}
                <button
                  onClick={toggleExamSelectionMode}
                  className="button -sm bg-light-4 text-light-1 fw-500"
                >
                  Exit Selection Mode
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced User Table with DataGrid */}
      <div className="col-12">
        <div className="d-flex items-center justify-between mb-20">
          <h5 className="text-18 lh-1 fw-500">Users List</h5>
          {!isExamSelectionMode && (
            <button
              onClick={toggleExamSelectionMode}
              className="button -sm bg-purple-1 text-white fw-500"
            >
              <i className="icon-check text-16 mr-10"></i>
              Select Users for Exam Assignment
            </button>
          )}
        </div>
        
        <DataGridUserTable 
          users={users}
          loading={loading}
          pagination={pagination}
          onStatusChange={handleUserStatus}
          onDelete={handleDeleteUser}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          searchTerm={searchTerm}
          filters={filters}
          sortConfig={sortConfig}
          // Selection props
          showSelection={isExamSelectionMode}
          selectedUsers={selectedUsers}
          onSelectionChange={handleUserSelectionChange}
        />
      </div>

      {/* Bulk Upload Results Modal */}
      {bulkResults && (
        <BulkUploadResults 
          results={bulkResults}
          onClose={() => setBulkResults(null)}
        />
      )}

      {/* Bulk Progress Modal */}
      {bulkProgress && (
        <BulkProgressModal 
          progress={bulkProgress}
          onClose={() => setBulkProgress(null)}
        />
      )}

      {/* Exam Assignment Modal */}
      {showExamAssignment && (
        <ExamAssignmentModal
          isOpen={showExamAssignment}
          onClose={() => setShowExamAssignment(false)}
          selectedUsers={selectedUsers}
          onAssignExams={handleAssignExams}
        />
      )}
    </div>
  );
};

export default UserManagement; 